import React, { useState, useEffect } from 'react';
import { api } from '../services/api';

function ManagerDashboard() {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const data = await api.getManagerDashboard();
      setDashboard(data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (type) => {
    const icons = {
      claimed: '✋',
      unclaimed: '🔄',
      reassigned: '↔️',
      stage_change: '📊',
      contacted: '📞',
    };
    return icons[type] || '📝';
  };

  if (loading) {
    return <div style={styles.loading}>Loading dashboard...</div>;
  }

  if (!dashboard) {
    return <div style={styles.loading}>Failed to load dashboard</div>;
  }

  const { metrics, staff_performance, recent_activity, stuck_leads } = dashboard;

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Manager Dashboard</h1>

      {/* Metrics Overview */}
      <div style={styles.metricsGrid}>
        <div style={styles.metricCard}>
          <div style={styles.metricValue}>{metrics.total_leads}</div>
          <div style={styles.metricLabel}>Total Leads</div>
        </div>
        <div style={{...styles.metricCard, borderColor: '#f59e0b'}}>
          <div style={{...styles.metricValue, color: '#f59e0b'}}>{metrics.unclaimed_leads}</div>
          <div style={styles.metricLabel}>Unclaimed</div>
        </div>
        <div style={{...styles.metricCard, borderColor: 'var(--brand-primary, #3b82f6)'}}>
          <div style={{...styles.metricValue, color: 'var(--brand-primary, #3b82f6)'}}>{metrics.claimed_leads}</div>
          <div style={styles.metricLabel}>Claimed</div>
        </div>
        <div style={{...styles.metricCard, borderColor: '#22c55e'}}>
          <div style={{...styles.metricValue, color: '#22c55e'}}>{metrics.converted_leads}</div>
          <div style={styles.metricLabel}>Converted</div>
        </div>
        <div style={styles.metricCard}>
          <div style={styles.metricValue}>{metrics.conversion_rate.toFixed(1)}%</div>
          <div style={styles.metricLabel}>Conversion Rate</div>
        </div>
        <div style={styles.metricCard}>
          <div style={styles.metricValue}>{metrics.leads_this_week}</div>
          <div style={styles.metricLabel}>This Week</div>
        </div>
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        <button
          onClick={() => setActiveTab('overview')}
          style={{
            ...styles.tab,
            ...(activeTab === 'overview' ? styles.tabActive : {}),
          }}
        >
          👥 Team Performance
        </button>
        <button
          onClick={() => setActiveTab('activity')}
          style={{
            ...styles.tab,
            ...(activeTab === 'activity' ? styles.tabActive : {}),
          }}
        >
          📋 Recent Activity
        </button>
        <button
          onClick={() => setActiveTab('alerts')}
          style={{
            ...styles.tab,
            ...(activeTab === 'alerts' ? styles.tabActive : {}),
          }}
        >
          ⚠️ Alerts ({stuck_leads.length})
        </button>
      </div>

      {/* Tab Content */}
      <div style={styles.tabContent}>
        {activeTab === 'overview' && (
          <div style={styles.staffGrid}>
            {staff_performance.map(staff => (
              <div key={staff.user_id} style={styles.staffCard}>
                <div style={styles.staffHeader}>
                  <div style={styles.staffAvatar}>
                    {staff.full_name.charAt(0)}
                  </div>
                  <div>
                    <div style={styles.staffName}>{staff.full_name}</div>
                    <div style={styles.staffRole}>{staff.role}</div>
                  </div>
                </div>

                <div style={styles.staffStats}>
                  <div style={styles.staffStat}>
                    <div style={styles.staffStatValue}>{staff.leads_claimed}</div>
                    <div style={styles.staffStatLabel}>Leads</div>
                  </div>
                  <div style={styles.staffStat}>
                    <div style={styles.staffStatValue}>{staff.leads_converted}</div>
                    <div style={styles.staffStatLabel}>Converted</div>
                  </div>
                  <div style={styles.staffStat}>
                    <div style={{...styles.staffStatValue, color: staff.conversion_rate >= 50 ? '#22c55e' : '#f59e0b'}}>
                      {staff.conversion_rate}%
                    </div>
                    <div style={styles.staffStatLabel}>Rate</div>
                  </div>
                </div>

                <div style={styles.activitiesCount}>
                  {staff.total_activities} activities this period
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'activity' && (
          <div style={styles.activityList}>
            {recent_activity.length === 0 ? (
              <div style={styles.emptyState}>No recent activity</div>
            ) : (
              recent_activity.map((activity, index) => (
                <div key={index} style={styles.activityItem}>
                  <div style={styles.activityIcon}>
                    {getActivityIcon(activity.type)}
                  </div>
                  <div style={styles.activityContent}>
                    <div style={styles.activityText}>
                      <strong>{activity.performed_by}</strong> {activity.description} on <strong>{activity.lead_name}</strong>
                    </div>
                    <div style={styles.activityTime}>
                      {new Date(activity.created_at).toLocaleString('en-AU')}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'alerts' && (
          <div style={styles.alertsList}>
            {stuck_leads.length === 0 ? (
              <div style={styles.emptyState}>No stuck leads! Great job team!</div>
            ) : (
              stuck_leads.map(lead => (
                <div key={lead.lead_id} style={styles.alertItem}>
                  <div style={styles.alertIcon}>⚠️</div>
                  <div style={styles.alertContent}>
                    <div style={styles.alertTitle}>
                      {lead.lead_name} stuck for {lead.days_stuck} days
                    </div>
                    <div style={styles.alertDetail}>
                      Claimed by: {lead.claimed_by_name}
                      {lead.last_activity && ` • Last activity: ${new Date(lead.last_activity).toLocaleDateString('en-AU')}`}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '1000px',
    padding: '20px',
  },
  title: {
    color: '#f1f5f9',
    margin: '0 0 24px 0',
    fontSize: '24px',
    fontWeight: '600',
  },
  loading: {
    color: '#94a3b8',
    fontSize: '14px',
    padding: '20px',
  },
  metricsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: '12px',
    marginBottom: '24px',
  },
  metricCard: {
    background: '#1e293b',
    borderRadius: '12px',
    padding: '20px',
    textAlign: 'center',
    border: '1px solid #334155',
    borderTop: '3px solid #334155',
  },
  metricValue: {
    color: '#f1f5f9',
    fontSize: '28px',
    fontWeight: '700',
  },
  metricLabel: {
    color: '#64748b',
    fontSize: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginTop: '4px',
  },
  tabs: {
    display: 'flex',
    gap: '8px',
    marginBottom: '20px',
    borderBottom: '1px solid #334155',
    paddingBottom: '12px',
  },
  tab: {
    padding: '10px 20px',
    background: 'transparent',
    border: 'none',
    borderRadius: '8px',
    color: '#94a3b8',
    fontSize: '14px',
    cursor: 'pointer',
  },
  tabActive: {
    background: 'var(--brand-primary, #3b82f6)',
    color: '#fff',
  },
  tabContent: {
    minHeight: '300px',
  },
  staffGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '16px',
  },
  staffCard: {
    background: '#1e293b',
    borderRadius: '12px',
    padding: '20px',
    border: '1px solid #334155',
  },
  staffHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '16px',
  },
  staffAvatar: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    background: 'var(--brand-primary, #3b82f6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: '20px',
    fontWeight: '600',
  },
  staffName: {
    color: '#f1f5f9',
    fontSize: '16px',
    fontWeight: '600',
  },
  staffRole: {
    color: '#64748b',
    fontSize: '12px',
    textTransform: 'uppercase',
  },
  staffStats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '12px',
    marginBottom: '12px',
  },
  staffStat: {
    textAlign: 'center',
  },
  staffStatValue: {
    color: '#f1f5f9',
    fontSize: '20px',
    fontWeight: '700',
  },
  staffStatLabel: {
    color: '#64748b',
    fontSize: '11px',
  },
  activitiesCount: {
    color: '#94a3b8',
    fontSize: '12px',
    textAlign: 'center',
    paddingTop: '12px',
    borderTop: '1px solid #334155',
  },
  activityList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  activityItem: {
    display: 'flex',
    gap: '12px',
    padding: '16px',
    background: '#1e293b',
    borderRadius: '8px',
    border: '1px solid #334155',
  },
  activityIcon: {
    fontSize: '20px',
  },
  activityContent: {
    flex: 1,
  },
  activityText: {
    color: '#f1f5f9',
    fontSize: '14px',
    lineHeight: '1.5',
  },
  activityTime: {
    color: '#64748b',
    fontSize: '12px',
    marginTop: '4px',
  },
  alertsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  alertItem: {
    display: 'flex',
    gap: '12px',
    padding: '16px',
    background: 'rgba(239, 68, 68, 0.1)',
    borderRadius: '8px',
    border: '1px solid rgba(239, 68, 68, 0.3)',
  },
  alertIcon: {
    fontSize: '20px',
  },
  alertContent: {
    flex: 1,
  },
  alertTitle: {
    color: '#f1f5f9',
    fontSize: '14px',
    fontWeight: '500',
  },
  alertDetail: {
    color: '#94a3b8',
    fontSize: '12px',
    marginTop: '4px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px',
    color: '#64748b',
    fontSize: '14px',
  },
};

export default ManagerDashboard;
